import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { Folder, ChevronRight, Home, ArrowLeft, Check, FolderOpen, Loader } from 'lucide-react';
import { TFileSubfoldersItem } from '@/data/types';
import { getFilePathsAsync } from '@/data/store/actions';
import { useAppDispatch } from '@/data/store/utils/redux';

interface FilePathsProps {
  currentPath: string;
  initialPath: string;
  onSelectPath: (path: string) => void;
  onCancel: () => void;
  fileName: string;
}

const FilePaths: React.FC<FilePathsProps> = ({ currentPath, onSelectPath, onCancel, fileName, initialPath }) => {
  const dispatch = useAppDispatch();
  const [selectedPath, setSelectedPath] = useState<string>(initialPath);
  const [navigationHistory, setNavigationHistory] = useState<string[]>(['/']);
  const [availableFolders, setAvailableFolders] = useState<TFileSubfoldersItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Fetch folders whenever selectedPath changes
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        setIsLoading(true);
        const result = await dispatch(getFilePathsAsync({ path: selectedPath })).unwrap();
        setAvailableFolders(result.subfolders || []);
      } catch (error) {
        console.error('Failed to fetch folders:', error);
        setAvailableFolders([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFolders();
  }, [dispatch, selectedPath]);

  // Get folders for the current selected path
  const currentFolders = useMemo(() => {
    return availableFolders.filter((folder: TFileSubfoldersItem) => {
      if (selectedPath === '/') {
        // Root level: show folders that don't have a parent (only one level deep)
        return folder.directory.split('/').filter(Boolean).length === 1;
      } else {
        // Show immediate subfolders of the selected path
        const pathDepth = selectedPath.split('/').filter(Boolean).length;
        return (
          folder.directory.startsWith(selectedPath + '/') &&
          folder.directory.split('/').filter(Boolean).length === pathDepth + 1
        );
      }
    });
  }, [availableFolders, selectedPath]);

  // Get breadcrumb segments
  const breadcrumbSegments = useMemo(() => {
    if (selectedPath === '/') return [];
    return selectedPath.split('/').filter(Boolean);
  }, [selectedPath]);

  const handleFolderClick = useCallback(
    (folderPath: string) => {
      setNavigationHistory((prev) => [...prev, selectedPath]);
      setSelectedPath(folderPath);
    },
    [selectedPath],
  );

  const handleBreadcrumbClick = useCallback(
    (index: number) => {
      if (index === -1) {
        // Root clicked
        setSelectedPath('/');
      } else {
        const segments = selectedPath.split('/').filter(Boolean);
        const newPath = '/' + segments.slice(0, index + 1).join('/');
        setSelectedPath(newPath);
      }
    },
    [selectedPath],
  );

  const handleBackClick = useCallback(() => {
    if (navigationHistory.length > 1) {
      const previousPath = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory((prev) => prev.slice(0, -1));
      setSelectedPath(previousPath);
    } else {
      // Go to parent directory
      const segments = selectedPath.split('/').filter(Boolean);
      if (segments.length > 0) {
        const parentPath = segments.length === 1 ? '/' : '/' + segments.slice(0, -1).join('/');
        setSelectedPath(parentPath);
      }
    }
  }, [navigationHistory, selectedPath]);

  const handleConfirm = useCallback(() => {
    onSelectPath(selectedPath);
  }, [selectedPath, onSelectPath]);

  const canGoBack = selectedPath !== '/' || navigationHistory.length > 1;

  return (
    <div
      className="mx-6 flex h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 px-8 py-6">
        <div className="flex items-center gap-4">
          <div className="rounded-2xl bg-blue-100 p-3">
            <FolderOpen className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Μετακίνηση αρχείου</h2>
            <p className="mt-2 text-base text-gray-600">
              Επιλέξτε νέο προορισμό για το αρχείο:{' '}
              <span className="rounded-md bg-blue-50 px-2 py-1 font-semibold text-blue-700">{fileName}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="border-b border-gray-100 bg-gray-50/50 px-8 py-4">
        <div className="flex items-center gap-3">
          {/* Back Button */}
          <button
            onClick={handleBackClick}
            disabled={!canGoBack || isLoading}
            className="rounded-xl p-3 text-gray-500 transition-all duration-200 hover:bg-white hover:text-gray-700 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gray-500 disabled:hover:shadow-none"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          {/* Breadcrumb */}
          <div className="scrollbar-hide flex items-center gap-2 overflow-x-auto">
            <button
              onClick={() => handleBreadcrumbClick(-1)}
              disabled={isLoading}
              className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-gray-700 transition-all duration-200 hover:bg-white hover:text-blue-700 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Home className="h-4 w-4" />
              <span>Root</span>
            </button>

            {breadcrumbSegments.map((segment, index) => (
              <React.Fragment key={index}>
                <ChevronRight className="h-4 w-4 text-gray-400" />
                <button
                  onClick={() => handleBreadcrumbClick(index)}
                  disabled={isLoading}
                  className="rounded-xl px-4 py-2 text-sm font-semibold text-gray-700 transition-all duration-200 hover:bg-white hover:text-blue-700 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {segment}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Folder List */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-gray-500">
            <div className="text-center">
              <div className="mx-auto mb-4 rounded-full bg-blue-100 p-4">
                <Loader className="mx-auto h-12 w-12 animate-spin text-blue-600" />
              </div>
              <h3 className="mb-2 text-lg font-medium text-gray-700">Φόρτωση φακέλων...</h3>
              <p className="text-sm text-gray-500">Παρακαλώ περιμένετε</p>
            </div>
          </div>
        ) : currentFolders.length === 0 ? (
          <div className="flex h-full items-center justify-center text-gray-500">
            <div className="text-center">
              <div className="mx-auto mb-4 rounded-full bg-gray-100 p-4">
                <Folder className="mx-auto h-12 w-12 opacity-50" />
              </div>
              <h3 className="mb-2 text-lg font-medium text-gray-700">Δεν υπάρχουν φάκελοι</h3>
              <p className="text-sm text-gray-500">Αυτός ο φάκελος δεν περιέχει υποφακέλους</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {currentFolders.map((folder) => {
              const folderName = folder.directory.split('/').pop() || '';
              return (
                <motion.button
                  key={folder.directory}
                  onClick={() => handleFolderClick(folder.directory)}
                  disabled={isLoading}
                  whileHover={{ scale: 1.02, x: 8 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex w-full items-center gap-4 rounded-2xl border border-transparent p-5 text-left transition-all duration-200 hover:border-blue-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <div className="rounded-xl bg-blue-100 p-3">
                    <Folder className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-gray-900">{folderName}</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {folder.count} αρχεί{folder.count === 1 ? 'ο' : 'α'}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 transition-transform group-hover:translate-x-1" />
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Path Display */}
      <div className="border-t border-gray-100 bg-gray-50/50 px-8 py-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600">Επιλεγμένος προορισμός:</span>
          <code className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 font-mono text-sm font-medium text-blue-700">
            {selectedPath === '/' ? 'Root' : selectedPath}
          </code>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between border-t border-gray-100 bg-white px-8 py-6">
        <div className="text-sm text-gray-500">Επιλέξτε έναν φάκελο για να μετακινήσετε το αρχείο</div>
        <div className="flex items-center gap-4">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="rounded-xl border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Ακύρωση
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedPath === currentPath || isLoading}
            className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:from-blue-600 disabled:hover:to-indigo-600 disabled:hover:shadow-none"
          >
            <Check className="h-4 w-4" />
            Μετακίνηση εδώ
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilePaths;
